import jsPDF from "jspdf";

import ArialNormal from "../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../utils/Customfonts/ARIALBI.TTF";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL LAYOUT CONFIG
// -----------------------------------------------------------------------------
// This design intentionally mirrors "Annexure - 3 / Job Description":
// a plain, un-boxed form (label: value lines), bulleted Duties/Authorities,
// underlined-label footer fields, and a fixed bottom footer with the
// document number / revision info + page number.
// -----------------------------------------------------------------------------

export const config = {
  margin: 40,

  fontSize: 10.5,
  labelFontSize: 10.5,
  titleFontSize: 15,
  docNumberFontSize: 10,

  lineGap: 18, // vertical space consumed per single text line
  bulletIndent: 14,
  bulletGap: 6, // extra gap between bullet items
  sectionGap: 16, // gap after a heading / before a new section

  get contentTop() {
    return 118; // below the header block (title + document number) - PAGE 1 ONLY
  },

  // Continuation pages don't redraw the header, so they only need a small
  // top margin instead of the full header offset used on page 1.
  continuationTop: 20,

  get contentBottom() {
    return 70; // reserved for the fixed bottom footer
  },
};

// -----------------------------------------------------------------------------
// FIELD DEFINITIONS (kept in sync with the form's `inputFields` config)
// -----------------------------------------------------------------------------

export const JOB_DESCRIPTION_FIELDS = [
  { type: "Autocomplete", label: "Designation", stateKey: "designation" },
  { type: "TextField", label: "Grade", stateKey: "grade" },
  { type: "TextField", label: "Reports to", stateKey: "reportsToId" },
];

export const JOB_DESCRIPTION_TEXTAREAS = [
  { label: "Duties", stateKey: "duties" },
  { label: "Authorities", stateKey: "authorities" },
];

export const JOB_DESCRIPTION_FOOTER_FIELDS = [
  { label: "Education", stateKey: "education" },
  { label: "Experience", stateKey: "experience" },
];

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";

  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// Normalizes a Duties/Authorities value into an array of clean bullet lines.
//
// The tricky real-world case: pasted content (from Word, or copied out of
// a previous PDF) often has a hard line break (\n) at every visually
// wrapped line, NOT only at the end of each logical bullet/sentence. If we
// split naively on every \n, a single duty like:
//   "Ensure that inspection procedures, documentation, and reports are in
//    line with project requirements and industry standards."
// gets cut into two separate bullets:
//   "• Ensure that ... in line with project"
//   "• requirements and industry standards."
// which is exactly the fragmented/extra-spaced output being reported.
//
// Fix: only start a NEW bullet once a line actually finishes a sentence
// (ends in . ; : ! ?) or an explicit "•" marker is hit. Lines that don't
// end a sentence get merged into the next line instead of becoming their
// own bullet.
const toBulletList = (value) => {
  const normalize = (raw) => String(raw).replace(/\u00A0/g, " ");

  const stripMarkers = (line) =>
    line
      .replace(/^[\s\-*]+/, "") // strip leading dash/asterisk/space
      .replace(/^\d+[.)]\s*/, "") // strip leading "1." / "2)" numbering
      .trim();

  // Array input: assume each element is already one complete bullet.
  if (Array.isArray(value)) {
    return value
      .map((item) => stripMarkers(normalize(item)))
      .filter((item) => /[a-zA-Z0-9]/.test(item));
  }

  const text = normalize(value || "");

  // Case 1: source already has explicit "•" bullet markers. Split on
  // those; any \n found INSIDE one "•" segment is just a wrapped
  // continuation line, so join those back together with a space instead
  // of treating them as separate bullets.
  if (text.includes("•")) {
    return text
      .split("•")
      .map((segment) =>
        segment
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .join(" ")
      )
      .map(stripMarkers)
      .filter((item) => /[a-zA-Z0-9]/.test(item));
  }

  // Case 2: no explicit bullet markers, just line breaks — reconstruct
  // full sentences by merging lines until one ends with terminal
  // punctuation, then start the next bullet.
  const rawLines = text
    .split(/\r?\n/)
    .map(stripMarkers)
    .filter(Boolean);

  const bullets = [];
  let buffer = "";

  rawLines.forEach((line) => {
    buffer = buffer ? `${buffer} ${line}` : line;
    if (/[.;:!?]$/.test(line)) {
      bullets.push(buffer.trim());
      buffer = "";
    }
  });

  // Flush any trailing text that never hit terminal punctuation.
  if (buffer.trim()) {
    bullets.push(buffer.trim());
  }

  return bullets.filter((item) => /[a-zA-Z0-9]/.test(item));
};

// Draws an array of pre-wrapped lines at an EXPLICIT per-line y position
// using config.lineGap, instead of handing the array straight to
// doc.text() (which uses jsPDF's own internal line-height and is what
// caused the extra empty space between bullets/lines).
const drawWrappedLines = (doc, lines, x, startY, lineGap = config.lineGap) => {
  lines.forEach((line, i) => {
    doc.text(line, x, startY + i * lineGap);
  });
};

// -----------------------------------------------------------------------------
// LAYOUT MANAGER
// -----------------------------------------------------------------------------

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;

    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;

    this.y = config.contentTop;
  },

  advance(amount) {
    this.y += amount;
  },

  ensureSpace(requiredHeight = 20) {
    if (this.y + requiredHeight > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      // Continuation pages have no header, so start near the top margin
      // instead of jumping down to config.contentTop.
      this.y = config.continuationTop;
      return true;
    }
    return false;
  },
};

// -----------------------------------------------------------------------------
// HEADER (Annexure title + document number)
// -----------------------------------------------------------------------------

const drawHeader = (doc, data) => {
  const centerX = layout.pageWidth / 2;

  // Logo (top-right)
  try {
    const logoSize = 46;
    const logoX = layout.pageWidth - config.margin - logoSize;
    const logoY = 18;

    try {
      doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
    } catch (_) {
      try {
        doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
      } catch (__) {
        doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
      }
    }
  } catch (_) {
    // Logo is optional
  }

  // Title block, centered
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.titleFontSize);
  doc.setTextColor(0, 0, 0);
  doc.text(data.annexureTitle || "Annexure - 3", centerX, 38, { align: "center" });
  doc.text("Job Description", centerX, 58, { align: "center" });

  // Document number, right aligned
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.docNumberFontSize);
  doc.text(
    `Document Number- ${data.documentNumber || "DYC/JD/DTM-MECH Rev.00"}`,
    layout.pageWidth - config.margin,
    88,
    { align: "right" }
  );
};

// -----------------------------------------------------------------------------
// LABEL : VALUE LINE  (e.g. "Designation: Deputy Technical Manager Mechanical")
// -----------------------------------------------------------------------------

const drawLabelValueLine = (doc, label, value, underlineLabel = false) => {
  layout.ensureSpace(config.lineGap);

  const x = config.margin;
  const y = layout.y;

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.labelFontSize);
  const labelText = `${label}:`;
  doc.text(labelText, x, y);
  const labelWidth = doc.getTextWidth(labelText);

  if (underlineLabel) {
    doc.setLineWidth(0.6);
    doc.line(x, y + 2, x + labelWidth, y + 2);
  }

  const valueX = x + labelWidth + 6;
  const maxWidth = layout.pageWidth - config.margin - valueX;

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  const valueLines = doc.splitTextToSize(String(value || ""), maxWidth);

  // Draw each wrapped line at an explicit y so spacing matches
  // config.lineGap exactly (fixes the extra-gap bug).
  drawWrappedLines(doc, valueLines, valueX, y);

  layout.advance(Math.max(config.lineGap, valueLines.length * config.lineGap));
};

// -----------------------------------------------------------------------------
// SECTION HEADING (e.g. "Duties:", "Requirements of Position:")
// -----------------------------------------------------------------------------

const drawSectionHeading = (doc, label) => {
  layout.ensureSpace(config.lineGap);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.labelFontSize);
  doc.text(`${label}:`, config.margin, layout.y);

  layout.advance(config.lineGap);
};

// -----------------------------------------------------------------------------
// BULLET LIST (Duties / Authorities)
// -----------------------------------------------------------------------------

const drawBulletList = (doc, items) => {
  const x = config.margin;
  const bulletX = x + 6;
  const textX = x + config.bulletIndent;
  const maxWidth = layout.pageWidth - config.margin - textX;

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.setFontSize(config.fontSize);

  // Drop anything that's blank after wrapping so a stray empty entry never
  // draws a bullet with nothing next to it (the source of the empty line).
  const list = items
    .map((item) => String(item).trim())
    .filter(Boolean);

  list.forEach((item, index) => {
    const lines = doc
      .splitTextToSize(item, maxWidth)
      .filter((line) => line.trim().length > 0);

    if (!lines.length) return;

    const blockHeight = lines.length * config.lineGap;

    layout.ensureSpace(blockHeight);

    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.text("\u2022", bulletX, layout.y);

    // Draw each wrapped line manually at layout.y + i*lineGap instead of
    // doc.text(lines, textX, layout.y) — that call used jsPDF's internal
    // line-height (smaller than config.lineGap), so the block visually
    // ended before layout.advance() caught up, leaving a gap before the
    // next bullet.
    drawWrappedLines(doc, lines, textX, layout.y);

    layout.advance(blockHeight);

    // Small gap between bullets only — not after the last one, so the
    // following heading doesn't get pushed down by a double gap.
    if (index < list.length - 1) {
      layout.advance(config.bulletGap);
    }
  });
};

// -----------------------------------------------------------------------------
// SIGNATURE BLOCK (Employee Name / Signature / Date)
// -----------------------------------------------------------------------------

const drawSignatureBlock = (doc, data) => {
  layout.ensureSpace(config.lineGap * 3 + 20);

  layout.advance(10);
  doc.setLineWidth(0.5);
  doc.line(config.margin, layout.y, layout.pageWidth - config.margin, layout.y);
  layout.advance(20);

  drawLabelValueLine(doc, "Employee Name", data.employeeName, true);

  // Signature line: draw the signature image if provided, otherwise leave
  // the underlined label with blank space for a wet signature.
  layout.ensureSpace(config.lineGap + 20);
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.labelFontSize);
  const sigLabel = "Signature:";
  doc.text(sigLabel, config.margin, layout.y);
  const sigLabelWidth = doc.getTextWidth(sigLabel);
  doc.setLineWidth(0.6);
  doc.line(config.margin, layout.y + 2, config.margin + sigLabelWidth, layout.y + 2);

  if (data.signatureImage) {
    try {
      doc.addImage(
        data.signatureImage,
        config.margin + sigLabelWidth + 10,
        layout.y - 22,
        90,
        28
      );
    } catch (error) {
      console.warn("Unable to add signature image", error);
    }
  }

  layout.advance(config.lineGap + 6);

  drawLabelValueLine(doc, "Date", data.signedDate || data.jobDescriptionDate, true);
};

// -----------------------------------------------------------------------------
// FIXED BOTTOM FOOTER (form/revision info + page number)
// -----------------------------------------------------------------------------

const addPageFooters = (doc, totalPages, data = {}) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    const footerY = pageHeight - 34;

    doc.setLineWidth(0.5);
    doc.line(config.margin, footerY - 12, pageWidth - config.margin, footerY - 12);

    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    doc.text(
      data.formNumber || "DYC/FMT-HR/01 Rev.: 00 Eff. Dated- 24/05/2023",
      config.margin,
      footerY
    );
    doc.text(
      "Company General Use. All Rights Reserved by DYC Global Pvt Ltd",
      config.margin,
      footerY + 12
    );

    doc.text(`Page ${page} of ${totalPages}`, pageWidth - config.margin, footerY, {
      align: "right",
    });
  }
};

// -----------------------------------------------------------------------------
// MAIN PDF GENERATOR
// -----------------------------------------------------------------------------
// Usage:
//   await generateJobDescriptionPDF({
//     designation: "",
//     grade: "",
//     reportsTo: "",
//     duties: "",       // string (one bullet per line) or string[]
//     authorities: "",  // string (one bullet per line) or string[]
//     requirements: "", // optional free text under "Requirements of Position:"
//     education: "",
//     experience: "",
//     date: "",
//     employeeName: "", // optional, defaults to designation-holder's name
//     signatureImage: "", // optional base64/data-url image
//     documentNumber: "", // optional, defaults to "DYC/JD/DTM-MECH Rev.00"
//   });
// -----------------------------------------------------------------------------

export const generateJobDescriptionPDF = async (data = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(
      doc,
      ArialNormal,
      "Arial-Normal",
      customFontFamily.normalFont,
      customFontFamily.normal
    ),
    loadAndAddFont(
      doc,
      ArialBold,
      "Arial-Bold",
      customFontFamily.boldFont,
      customFontFamily.bold
    ),
    loadAndAddFont(
      doc,
      ArialItalic,
      "Arial-Italic",
      customFontFamily.italicFont,
      customFontFamily.italic
    ),
    loadAndAddFont(
      doc,
      ArialBoldItalic,
      "Arial-BoldItalic",
      customFontFamily.normalFont,
      "bolditalic"
    ),
  ]);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  layout.init(doc);
  drawHeader(doc, data);

  drawLabelValueLine(doc, "Designation", data.designation);
  drawLabelValueLine(doc, "Grade", data.grade);
  drawLabelValueLine(doc, "Reports to", data.reportsToId);

  layout.advance(4);
  drawSectionHeading(doc, "Duties");
  drawBulletList(doc, toBulletList(data.duties));

  layout.advance(config.sectionGap);
  drawSectionHeading(doc, "Authorities");
  drawBulletList(doc, toBulletList(data.authorities));

  layout.advance(config.sectionGap);
  drawSectionHeading(doc, "Requirements of Position");

  if (data.requirements) {
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(config.fontSize);
    const reqLines = doc.splitTextToSize(String(data.requirements), layout.contentWidth);
    layout.ensureSpace(reqLines.length * config.lineGap);

    // Draw each wrapped line manually (same fix as everywhere else) so the
    // block height matches what layout.advance() moves down by.
    drawWrappedLines(doc, reqLines, config.margin, layout.y);

    layout.advance(reqLines.length * config.lineGap);
  }

  layout.advance(10);
  drawLabelValueLine(doc, "Education", data.education, true);
  layout.advance(6);
  drawLabelValueLine(doc, "Experience", data.experience, true);

  drawSignatureBlock(doc, data);

  const totalPages = doc.internal.getNumberOfPages();
  addPageFooters(doc, totalPages, data);

  return doc;
};

export default generateJobDescriptionPDF;